import type { LeaveDetailPayload, LeaveRuleResult, LeaveTypeId } from '@lms/shared-types'
import type { LeaveCheckItem } from '../components/employee/LeaveCheck'
import { LEAVE_TYPE_LABEL } from '../lib/leave-type-meta'

/**
 * Deterministic team-calendar facts for a filed leave — the peers actually out
 * during the window and any blackout overlapping the dates. Derived from the
 * leave-detail `team_calendar`, the single source of truth the coverage /
 * blackout rows are stated from (so the grid and the panel can't disagree).
 */
export interface LeaveDetailFacts {
  teammatesOut: { count: number; names: string[] }
  overlappingBlackout: string | null
}

interface LeaveCheckModel {
  persistentItems: LeaveCheckItem[]
  items: LeaveCheckItem[]
}

function typeLabel(type: string): string {
  return LEAVE_TYPE_LABEL[type as LeaveTypeId] ?? type
}

/** Join display names as "a", "a and b", "a, b and c". */
function joinNames(names: readonly string[]): string {
  if (names.length <= 1) return names[0] ?? ''
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`
}

/** "unpaid_leave_taken" → "Unpaid leave taken". Mirrors the Leave Rhythm
 *  card's own code→label rendering so the two surfaces name patterns the same. */
function humanisePatternCode(code: string): string {
  const words = code.split('_').filter(Boolean)
  if (words.length === 0) return code
  const first = words[0] ?? ''
  return [first.charAt(0).toUpperCase() + first.slice(1), ...words.slice(1)].join(' ')
}

/**
 * Build the "At a glance" checklist for a filed leave — the same facts the
 * apply page states before submit (`LeaveCheck`), re-stated for a leave that
 * already exists. Replaces the dark ILMU panel: deterministic, no verdict,
 * leave-scoped. Coverage + blackout read straight from `facts`; the duration
 * row consults the live rule-engine result (`rule`) for a balance warning. The
 * pattern row combines the employee's current rhythm (history) with the patterns
 * approving THIS leave would newly trigger (from `rule.patterns`).
 */
export function buildLeaveCheckItems(
  leave: LeaveDetailPayload,
  rule: LeaveRuleResult | null,
  facts: LeaveDetailFacts,
  viewerIsOwner = false,
): LeaveCheckModel {
  const days = leave.days
  const dayWord = days === 1 ? 'day' : 'days'
  const isUnpaid = leave.type === 'unpaid'

  // Date-independent heads-up: the payroll consequence of unpaid leave. Terse
  // by design — the escalation detail belongs to the approver path, not here.
  const persistentItems: LeaveCheckItem[] = isUnpaid
    ? [
        {
          key: 'unpaid',
          status: 'attn',
          fact: <>You won&apos;t be paid for these days (affects payroll).</>,
        },
      ]
    : []

  const items: LeaveCheckItem[] = []

  // Duration / balance. Unpaid draws no balance, so state it as unpaid days.
  // Everything else states the duration, flipped to △ if the rule engine
  // raised an insufficient-balance warning.
  if (isUnpaid) {
    items.push({
      key: 'balance',
      status: 'ok',
      fact: (
        <>
          <strong style={{ color: 'var(--text)', fontWeight: 700 }}>{days}</strong> {dayWord} unpaid leave
          requested
        </>
      ),
    })
  } else {
    const insufficient = rule?.warnings.some((w) => w.code === 'insufficient_balance') ?? false
    items.push({
      key: 'balance',
      status: insufficient ? 'attn' : 'ok',
      fact: insufficient ? (
        <>
          <strong style={{ color: 'var(--text)', fontWeight: 700 }}>{days}</strong> {dayWord} requested — exceeds
          the available {typeLabel(leave.type).toLowerCase()} balance
        </>
      ) : (
        <>
          <strong style={{ color: 'var(--text)', fontWeight: 700 }}>{days}</strong> {dayWord} of{' '}
          {typeLabel(leave.type).toLowerCase()} leave
        </>
      ),
    })
  }

  // Coverage — peers out during the window, out of the team size.
  const { count } = facts.teammatesOut
  const teamSize = leave.team_calendar.team_size
  const coverageConcern =
    rule?.warnings.some((w) => w.code === 'coverage_concern_for_health_leave') ?? false
  items.push({
    key: 'coverage',
    status: count > 0 && coverageConcern ? 'attn' : 'ok',
    fact:
      count > 0 ? (
        <>
          <strong style={{ color: 'var(--text)', fontWeight: 700 }}>{count}</strong> teammate{count > 1 ? 's' : ''}{' '}
          also out on these dates ({count} of {teamSize}).{' '}
          {coverageConcern ? 'Coverage is tight, worth a look.' : 'Flagged so you can plan.'}
        </>
      ) : (
        'No teammates out on these dates.'
      ),
  })

  // Blackout overlap.
  items.push({
    key: 'blackout',
    status: facts.overlappingBlackout ? 'attn' : 'ok',
    fact: facts.overlappingBlackout ? (
      <>
        Overlaps <strong style={{ color: 'var(--text)', fontWeight: 700 }}>{facts.overlappingBlackout}</strong>.
      </>
    ) : (
      'No blackout overlap.'
    ),
  })

  // Pattern signals — two distinct questions, both surfaced so an approver
  // sees the full picture:
  //  • HISTORY: patterns the employee is already in, from
  //    health_bar.contributing_patterns (today-anchored — the same source the
  //    "Leave Rhythm" card reads, so the two never disagree).
  //  • TRIGGER: patterns THIS leave would newly push over threshold, from the
  //    draft preview (rule.patterns, anchored at the leave's own dates). This
  //    is why a clean-history employee filing a future long-weekend leave still
  //    gets flagged: history is steady, but approving it creates the pattern.
  const historyCodes = leave.health_bar?.contributing_patterns ?? []
  // Dedupe against history: health_bar (which counts the pending leave) and the
  // draft preview can flag the SAME pattern — health_bar reads it as active
  // while the preview reports triggeredByDraft. Without this guard the row
  // double-lists it ("1 active pattern: X. Approving this also adds X."). If
  // history already shows it, don't also list it as a new trigger.
  const triggered = (rule?.patterns ?? []).filter(
    (p) =>
      p.triggeredByDraft && !p.activeWithoutDraft && !historyCodes.includes(p.code),
  )
  const historyNames = joinNames(historyCodes.map(humanisePatternCode))
  const triggeredNames = joinNames(
    triggered.map((p) => p.displayName.replace(/\s*pattern$/i, '').trim()),
  )
  const bold = (text: string): JSX.Element => (
    <strong style={{ color: 'var(--text)', fontWeight: 700 }}>{text}</strong>
  )
  const hasHistory = historyCodes.length > 0
  const hasTrigger = triggered.length > 0
  // Owner sees their own leave and can't approve it, so phrase the trigger as
  // the leave's own effect ("this leave will…") rather than the approver's
  // action ("approving this will…"). Employees keep the flag — useful heads-up
  // that they could still adjust or cancel before it lands.
  const willTrigger = viewerIsOwner ? 'this leave will trigger' : 'approving this will trigger'
  const alsoAdds = viewerIsOwner ? 'This leave also adds' : 'Approving this also adds'
  items.push({
    key: 'pattern',
    status: hasHistory || hasTrigger ? 'attn' : 'ok',
    fact:
      hasHistory && hasTrigger ? (
        <>
          {historyCodes.length} active leave pattern
          {historyCodes.length > 1 ? 's' : ''}: {bold(historyNames)}. {alsoAdds}{' '}
          {bold(triggeredNames)}.
        </>
      ) : hasHistory ? (
        <>
          {historyCodes.length} active leave pattern
          {historyCodes.length > 1 ? 's' : ''}: {bold(historyNames)}.
        </>
      ) : hasTrigger ? (
        <>
          No active patterns in history, but {willTrigger} the {bold(triggeredNames)}{' '}
          pattern{triggered.length > 1 ? 's' : ''}.
        </>
      ) : (
        'No active patterns — leave rhythm is steady.'
      ),
  })

  return { persistentItems, items }
}
